export const HELP_SUPPORT_EMAIL = "support@konstrulog.com";
export const HELP_SALES_EMAIL = "contato@konstrulog.com";

export const HELP_FAQ_SECTION_IDS = [
  "account",
  "users",
  "plans",
  "projects",
  "menus",
  "diary",
  "catalog",
  "reports",
  "mobile",
] as const;

export type HelpFaqSectionId = (typeof HELP_FAQ_SECTION_IDS)[number];

export type HelpFaqItem = {
  id: string;
  section: HelpFaqSectionId;
};

/** Starter questions shown as cards in the empty chat (Deel-style). */
export const HELP_SUGGESTED_FAQ_IDS = ["howToLog", "createProject", "invite"] as const;

/** Articles listed under Learn more in the help menu. */
export const HELP_MENU_FAQ_IDS = ["howToLog", "invite", "generatePdf"] as const;

const HELP_SEARCH_STOPWORDS = new Set([
  "a",
  "ao",
  "as",
  "o",
  "os",
  "um",
  "uma",
  "de",
  "do",
  "da",
  "dos",
  "das",
  "em",
  "no",
  "na",
  "nos",
  "nas",
  "e",
  "ou",
  "que",
  "como",
  "para",
  "por",
  "com",
  "sem",
  "meu",
  "minha",
  "the",
  "and",
  "for",
  "how",
  "what",
  "does",
  "can",
  "do",
  "i",
  "my",
  "to",
  "of",
  "in",
  "on",
  "is",
]);

export const HELP_FAQ_ITEMS: readonly HelpFaqItem[] = [
  { id: "signIn", section: "account" },
  { id: "forgotPassword", section: "account" },
  { id: "google", section: "account" },
  { id: "inviteNeeded", section: "account" },
  { id: "onboarding", section: "account" },
  { id: "switchOrg", section: "account" },
  { id: "accessDisabled", section: "account" },
  { id: "roles", section: "users" },
  { id: "invite", section: "users" },
  { id: "usersVsWorkers", section: "users" },
  { id: "userLimit", section: "users" },
  { id: "disableVsRemove", section: "users" },
  { id: "whichPlans", section: "plans" },
  { id: "trialEnded", section: "plans" },
  { id: "createProject", section: "projects" },
  { id: "inactivateProject", section: "projects" },
  { id: "editInactiveProject", section: "projects" },
  { id: "createRecords", section: "projects" },
  { id: "projectMenu", section: "menus" },
  { id: "settingsMenu", section: "menus" },
  { id: "serviceFront", section: "projects" },
  { id: "dashboard", section: "projects" },
  { id: "howToLog", section: "diary" },
  { id: "onePerDay", section: "diary" },
  { id: "draftVsPublished", section: "diary" },
  { id: "editYesterday", section: "diary" },
  { id: "photos", section: "diary" },
  { id: "materialOnDiary", section: "diary" },
  { id: "materialsWhere", section: "catalog" },
  { id: "occurrences", section: "catalog" },
  { id: "documents", section: "catalog" },
  { id: "budgets", section: "catalog" },
  { id: "generatePdf", section: "reports" },
  { id: "whatAppDoes", section: "mobile" },
  { id: "appOnboarding", section: "mobile" },
  { id: "offline", section: "mobile" },
];

export function normalizeHelpSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export function helpItemQuestionKey(id: string): string {
  return `help.items.${id}.q`;
}

export function helpItemAnswerKey(id: string): string {
  return `help.items.${id}.a`;
}

export function helpSectionTitleKey(section: HelpFaqSectionId): string {
  return `help.sections.${section}`;
}

export function firstHelpName(displayName: string | null | undefined): string | null {
  const part = displayName?.trim().split(/\s+/)[0];
  return part && part.length > 0 ? part : null;
}

function tokenizeHelpText(value: string): string[] {
  return normalizeHelpSearch(value)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 3 && !HELP_SEARCH_STOPWORDS.has(token));
}

/** Best FAQ for a typed question, or null when nothing is close enough. */
export function findBestHelpFaqId(
  query: string,
  translate: (key: string) => string,
): string | null {
  const tokens = tokenizeHelpText(query);
  if (tokens.length === 0) return null;

  let bestId: string | null = null;
  let bestScore = 0;

  for (const item of HELP_FAQ_ITEMS) {
    const haystack = tokenizeHelpText(
      `${translate(helpItemQuestionKey(item.id))} ${translate(helpItemAnswerKey(item.id))}`,
    );
    const hits = tokens.filter((token) =>
      haystack.some((word) => word.includes(token) || token.includes(word)),
    ).length;
    const score = hits / tokens.length;
    if (score > bestScore) {
      bestScore = score;
      bestId = item.id;
    }
  }

  return bestScore >= 0.5 ? bestId : null;
}
